import React from 'react';
import { useEmergency } from '../contexts/EmergencyContext';

/**
 * EmergencyOverlay 컴포넌트
 * Emergency 상태일 때 전체 화면에 표시되는 오버레이
 */
const EmergencyOverlay = () => {
  const { emergencyState, acknowledgeEmergency } = useEmergency();

  // 🔔 Emergency가 비활성화되었거나 이미 확인된 경우 오버레이 숨김
  if (!emergencyState.isActive || emergencyState.isAcknowledged) {
    return null;
  }

  const handleAcknowledge = () => {
    acknowledgeEmergency();
  };

  return (
    <div 
      className="emergency-overlay active"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: 'rgba(139, 0, 0, 0.85)',
        zIndex: 100,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center'
      }}
    >
      {/* 상단 Emergency 배너 */}
      <div className="emergency-banner" style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '80px',
        backgroundColor: '#8B0000',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        borderBottom: '4px solid #FF0000',
        boxShadow: '0 4px 20px rgba(255, 0, 0, 0.5)'
      }}>
        <div className="emergency-flasher" style={{
          fontSize: '36px',
          fontWeight: 'bold',
          color: '#FFFFFF',
          textShadow: '2px 2px 4px rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          gap: '15px'
        }}>
          <span className="siren-icon">🚨</span>
          <span>EMERGENCY ALERT</span>
          <span className="siren-icon">🚨</span>
        </div>
      </div>

      {/* 중앙 메인 알람 */}
      <div className="emergency-main" style={{
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderRadius: '20px',
        padding: '40px',
        maxWidth: '600px',
        width: '90%',
        textAlign: 'center',
        border: '4px solid #FF0000',
        boxShadow: '0 10px 50px rgba(255, 0, 0, 0.7)'
      }}>
        {/* 회전하는 사이렌 아이콘 */}
        <div className="rotating-siren" style={{
          fontSize: '80px',
          marginBottom: '20px'
        }}>
          🚨
        </div>

        <h1 style={{
          color: '#8B0000',
          fontSize: '32px',
          fontWeight: 'bold',
          marginBottom: '15px',
          textShadow: '1px 1px 2px rgba(0, 0, 0, 0.3)'
        }}>
          CRITICAL SYSTEM EMERGENCY
        </h1>

        <div style={{
          color: '#333',
          fontSize: '18px',
          marginBottom: '25px',
          lineHeight: '1.6'
        }}>
          {emergencyState.triggeredBy.length === 1 ? (
            <div>
              <strong>긴급 상황이 감지되었습니다:</strong>
              <br />
              <span style={{ color: '#8B0000', fontWeight: 'bold' }}>
                {emergencyState.lastTrigger?.chartTitle}
              </span>
              <br />
              <span style={{ fontSize: '16px', color: '#666' }}>
                조건: {emergencyState.lastTrigger?.condition} (값: {emergencyState.lastTrigger?.value})
              </span>
            </div>
          ) : (
            <div>
              <strong>{emergencyState.triggeredBy.length}개의 차트에서 긴급 상황이 감지되었습니다</strong>
            </div>
          )}
        </div>

        {/* 문제 차트 목록 */}
        {emergencyState.triggeredBy.length > 1 && (
          <div style={{
            backgroundColor: '#FFF5F5',
            border: '2px solid #FF9999',
            borderRadius: '10px',
            padding: '15px',
            marginBottom: '25px',
            textAlign: 'left'
          }}>
            <strong style={{ color: '#8B0000', marginBottom: '10px', display: 'block' }}>
              문제 차트 목록:
            </strong>
            {emergencyState.triggeredBy.map((trigger, index) => (
              <div key={trigger.chartId} style={{
                padding: '8px 0',
                borderBottom: index < emergencyState.triggeredBy.length - 1 ? '1px solid #FFE0E0' : 'none'
              }}>
                <div style={{ fontWeight: 'bold', color: '#8B0000' }}>
                  {trigger.chartTitle}
                </div>
                <div style={{ fontSize: '14px', color: '#666' }}>
                  {trigger.condition} (값: {trigger.value})
                </div>
              </div>
            ))}
          </div>
        )}

        {/* 액션 버튼 - 간소화된 1개 버튼 */}
        <div style={{
          display: 'flex',
          justifyContent: 'center'
        }}>
          {/* 확인 버튼 (오버레이 숨김) */}
          <button
            onClick={handleAcknowledge}
            style={{
              padding: '15px 40px',
              fontSize: '18px',
              fontWeight: 'bold',
              color: '#FFFFFF',
              backgroundColor: '#28A745',
              border: 'none',
              borderRadius: '10px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: '0 6px 12px rgba(40, 167, 69, 0.4)'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#218838';
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 8px 16px rgba(40, 167, 69, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#28A745';
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 6px 12px rgba(40, 167, 69, 0.4)';
            }}
          >
            ✅ 확인 (알람 해제)
          </button>
        </div>

        {/* 시간 정보 */}
        <div style={{
          marginTop: '20px',
          fontSize: '14px',
          color: '#666',
          borderTop: '1px solid #E0E0E0',
          paddingTop: '15px'
        }}>
          <div>
            <strong>발생 시간:</strong> {new Date(emergencyState.timestamp).toLocaleString()}
          </div>
          <div style={{ color: '#FFF', marginTop: '10px', fontWeight: 'bold' }}>
            ⬇️ 확인 버튼을 눌러 알람을 해제하세요 ⬇️
          </div>
        </div>
      </div>

      {/* CSS 애니메이션 */}
      <style jsx>{`
        .emergency-flasher {
          animation: emergency-flash 1s ease-in-out infinite;
        }

        @keyframes emergency-flash {
          0%, 100% { 
            opacity: 1; 
            text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
          }
          50% { 
            opacity: 0.7; 
            text-shadow: 0 0 20px rgba(255, 255, 255, 0.8);
          }
        }

        .siren-icon {
          animation: siren-spin 2s linear infinite;
        }

        @keyframes siren-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .rotating-siren {
          animation: emergency-bounce 1s ease-in-out infinite;
        }

        @keyframes emergency-bounce {
          0%, 100% { 
            transform: scale(1) rotate(0deg); 
          }
          25% { 
            transform: scale(1.1) rotate(90deg); 
          }
          50% { 
            transform: scale(1.2) rotate(180deg); 
          }
          75% { 
            transform: scale(1.1) rotate(270deg); 
          }
        }

        .emergency-overlay.active {
          animation: overlay-pulse 2s ease-in-out infinite;
        }

        @keyframes overlay-pulse {
          0%, 100% { 
            background-color: rgba(139, 0, 0, 0.85);
          }
          50% { 
            background-color: rgba(139, 0, 0, 0.95);
          }
        }
      `}</style>
    </div>
  );
};

export default EmergencyOverlay; 